import { splitId } from "Components/elements/RVFlow/Shared/FlowNodes/DynamicMultiBranchNode/utils";
import { NodeType } from "Types/Journey";

import type { DynamicHandle } from "Components/elements/RVFlow/constants";

export type JourneyBranchHandle = {
    id: string;
    label: string;
    hidden?: boolean;
};

const DYNAMIC_HANDLE_SLOT_COUNT = 5;

type VariantRecord = {
    id?: number | string;
    name?: string;
    weight?: number;
    isActive?: boolean;
};

function isVariantRecord(value: unknown): value is VariantRecord {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function getVariants(data: Record<string, unknown> | undefined): Record<string, VariantRecord> | undefined {
    const variants = data?.variants;
    if (!variants || typeof variants !== "object") {
        return undefined;
    }
    // Decision AI stores variants as a Form.List array; A/B stores a keyed object.
    // OpenUI / shallow parsers sometimes keep the `variants` key but drop nested objects.
    const rawEntries: Array<[string, unknown]> = Array.isArray(variants)
        ? variants.map((value, index) => [`variant${index + 1}`, value])
        : Object.entries(variants as Record<string, unknown>);
    const entries = rawEntries.filter(([, value]) => isVariantRecord(value));
    if (entries.length === 0) {
        return undefined;
    }
    return Object.fromEntries(entries) as Record<string, VariantRecord>;
}

/** Shared variant iteration for preview branch handles and builder DynamicHandles. */
function forEachVariant(
    data: Record<string, unknown> | undefined,
    callback: (variant: VariantRecord, handleIndex: number, variantKey: string) => void
): void {
    const variants = getVariants(data);
    if (!variants) {
        return;
    }
    Object.entries(variants).forEach(([variantKey, variant], handleIndex) => {
        callback(variant, handleIndex, variantKey);
    });
}

function createEmptyDynamicHandles(): Array<DynamicHandle | undefined> {
    return Array.from({ length: DYNAMIC_HANDLE_SLOT_COUNT }, () => undefined);
}

function variantDisplayName(variant: VariantRecord | undefined): string {
    return typeof variant?.name === "string" ? variant.name.trim() : "";
}

function variantWeightLabel(variant: VariantRecord | undefined): string {
    return typeof variant?.weight === "number" ? `${variant.weight}%` : "";
}

function getAbTestBranchHandles(nodeId: string, data: Record<string, unknown> | undefined): JourneyBranchHandle[] {
    const handles: JourneyBranchHandle[] = [];
    forEachVariant(data, (variant, handleIndex, variantKey) => {
        const label = variantWeightLabel(variant);
        if (!label) {
            return;
        }
        handles.push({
            id: `${nodeId}-${variantKey}`,
            label,
            hidden: variant.isActive === false && handleIndex >= 2,
        });
    });
    return handles;
}

function getDecisionAiBranchHandles(nodeId: string, data: Record<string, unknown> | undefined): JourneyBranchHandle[] {
    const handles: JourneyBranchHandle[] = [];
    forEachVariant(data, (variant, handleIndex) => {
        const label = variantDisplayName(variant);
        if (!label) {
            return;
        }
        handles.push({
            id: splitId(nodeId, String(variant.id ?? handleIndex + 1)),
            label,
            hidden: handleIndex >= 3 && !variant.name,
        });
    });
    return handles;
}

function labelFromAbSourceHandle(
    nodeId: string,
    sourceHandle: string,
    data: Record<string, unknown> | undefined
): string {
    const variantKey = sourceHandle.startsWith(`${nodeId}-`) ? sourceHandle.slice(nodeId.length + 1) : sourceHandle;
    const variants = getVariants(data);
    const variant = variants?.[variantKey];
    const weightLabel = variantWeightLabel(variant);
    if (weightLabel) {
        return weightLabel;
    }
    const match = /^variant(\d+)$/i.exec(variantKey);
    return match ? `Variant ${match[1]}` : variantKey;
}

function labelFromDecisionSourceHandle(
    nodeId: string,
    sourceHandle: string,
    data: Record<string, unknown> | undefined
): string {
    const splitToken = "#split#";
    const splitIdValue = sourceHandle.includes(splitToken)
        ? sourceHandle.slice(sourceHandle.indexOf(splitToken) + splitToken.length)
        : sourceHandle.startsWith(`${nodeId}-`)
          ? sourceHandle.slice(nodeId.length + 1)
          : sourceHandle;

    const variants = getVariants(data);
    if (variants) {
        for (const variant of Object.values(variants)) {
            if (String(variant.id) === String(splitIdValue)) {
                const named = variantDisplayName(variant);
                if (named) {
                    return named;
                }
            }
        }
    }
    return splitIdValue ? `Path ${splitIdValue}` : "Path";
}

function previewBranchPorts(data: Record<string, unknown> | undefined): JourneyBranchHandle[] {
    const ids = data?.previewBranchHandleIds;
    const labels = data?.previewBranchLabels;
    if (!Array.isArray(ids) || !Array.isArray(labels) || ids.length === 0) {
        return [];
    }
    const handles: JourneyBranchHandle[] = [];
    ids.forEach((id, index) => {
        if (typeof id !== "string" || !id) {
            return;
        }
        const label = labels[index];
        handles.push({
            id,
            label: typeof label === "string" && label.trim() ? label.trim() : id,
        });
    });
    return handles;
}

function previewVariantNames(data: Record<string, unknown> | undefined): string[] {
    const names = data?.previewBranchLabels ?? data?.previewVariantNames;
    if (!Array.isArray(names)) {
        return [];
    }
    return names
        .filter((name): name is string => typeof name === "string" && name.trim() !== "")
        .map((name) => name.trim());
}

/**
 * When nested `variants` are omitted from the canvas flow, rebuild branch handles
 * from outgoing edge sourceHandles and optional flattened preview labels.
 */
function inferBranchHandlesFromSourceHandles(
    nodeType: NodeType,
    nodeId: string,
    data: Record<string, unknown> | undefined,
    outgoingHandles: Array<{ id: string; label?: string }>
): JourneyBranchHandle[] {
    const seen = new Set<string>();
    const handles: JourneyBranchHandle[] = [];
    const names = previewVariantNames(data);
    for (const outgoing of outgoingHandles) {
        if (!outgoing.id || seen.has(outgoing.id)) {
            continue;
        }
        seen.add(outgoing.id);
        const inferredLabel =
            nodeType === NodeType.ABTest
                ? labelFromAbSourceHandle(nodeId, outgoing.id, data)
                : labelFromDecisionSourceHandle(nodeId, outgoing.id, data);
        handles.push({
            id: outgoing.id,
            label: outgoing.label?.trim() || names[handles.length] || inferredLabel,
        });
    }
    return handles;
}

/** Branch source handles for multi-exit journey node types (conditional / A/B / Decision AI). */
export function getJourneyBranchHandles(
    nodeType: NodeType | undefined,
    nodeId: string,
    data: Record<string, unknown> | undefined,
    outgoingHandles: Array<{ id: string; label?: string }> = []
): JourneyBranchHandle[] | undefined {
    switch (nodeType) {
        case NodeType.Conditional:
            return [
                { id: `${nodeId}-true`, label: "Yes" },
                { id: `${nodeId}-false`, label: "No" },
            ];
        case NodeType.ABTest: {
            const flattened = previewBranchPorts(data);
            if (flattened.length > 0) {
                return flattened;
            }
            const handles = getAbTestBranchHandles(nodeId, data);
            if (handles.length > 0) {
                return handles;
            }
            if (outgoingHandles.length > 0) {
                return inferBranchHandlesFromSourceHandles(NodeType.ABTest, nodeId, data, outgoingHandles);
            }
            return undefined;
        }
        case NodeType.DecisionAI: {
            const flattened = previewBranchPorts(data);
            if (flattened.length > 0) {
                return flattened;
            }
            const handles = getDecisionAiBranchHandles(nodeId, data);
            if (handles.length > 0) {
                return handles;
            }
            if (outgoingHandles.length > 0) {
                return inferBranchHandlesFromSourceHandles(NodeType.DecisionAI, nodeId, data, outgoingHandles);
            }
            return undefined;
        }
        default:
            return undefined;
    }
}

/**
 * Builder DynamicHandle slots (fixed length 5) for multi-exit nodes.
 * Unused slots are undefined so connectors can stay mounted but hidden.
 */
export function getJourneyDynamicHandles(
    nodeType: NodeType | undefined,
    data: Record<string, unknown> | undefined
): Array<DynamicHandle | undefined> {
    const handles = createEmptyDynamicHandles();

    switch (nodeType) {
        case NodeType.ABTest:
            forEachVariant(data, (variant, handleIndex) => {
                handles[handleIndex] = {
                    displayValue: variant.weight,
                    connectorShouldDisplay: Boolean(variant.isActive),
                    name: variant.name,
                };
            });
            break;
        case NodeType.DecisionAI:
            forEachVariant(data, (variant, handleIndex) => {
                handles[handleIndex] = {
                    id: variant.id,
                    displayValue: variant.name,
                    connectorShouldDisplay: true,
                    name: variant.name,
                };
            });
            break;
        default:
            break;
    }

    return handles;
}
